import {
  type Answers,
  canAdvance,
  completion,
  FUNNEL,
  interpolate,
  nextScreen,
  progress,
  resolve,
  screenById,
  visibleOptions,
} from '@starter/funnel';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { FunnelCta, FunnelFrame, FunnelPanel, FunnelQuestion } from '../funnel/frame.tsx';
import {
  AnalysisLoader,
  AvatarPicker,
  Choice,
  EmailCapture,
  IntroHero,
  type KindProps,
  MultiChoice,
  PhotoUpload,
  Statement,
  TextInput,
} from '../funnel/kinds.tsx';
import { clearSaved, readSaved, writeSaved } from '../funnel/state.ts';

/**
 * `/start` — the acquisition funnel.
 *
 * **Public, and that is the whole point of it**: it runs before anybody has an
 * account, and every other screen in this application is behind a guard. It is
 * also why it needs no session, no tenant and no API — the answers live in the
 * browser until the chapter that turns them into a brand.
 *
 * The component is small because the funnel is data. It looks up one screen,
 * asks the engine which options this person may see and what the sentence says
 * with their answers in it, renders the one kind that screen names, and asks
 * the engine where to go when they are done. Nine screens ship today and
 * twenty-nine more arrive as rows in `@starter/funnel` rather than as code
 * here (`prd/funnel/README.md`).
 */
type KindRender = (
  props: KindProps & { values: string[]; onToggle: (key: string) => void },
) => React.ReactElement;

const KINDS: Record<string, KindRender> = {
  intro_hero: IntroHero,
  single_choice: (props) => <Choice {...props} />,
  image_choice: (props) => <Choice {...props} image />,
  multi_choice: MultiChoice,
  text_input: TextInput,
  photo_upload: PhotoUpload,
  /*
   * Five kinds, one component. They differ by the copy slots their screen
   * filled, not by a branch in here — which is what stops «add a screen that
   * says something» from meaning «add a component».
   */
  star_choice: (props) => <Choice {...props} image />,
  make_choice: (props) => <Choice {...props} image />,
  look_choice: (props) => <Choice {...props} image />,
  scene_choice: (props) => <Choice {...props} image />,
  avatar_picker: AvatarPicker,
  mirror_back: Statement,
  proof_beat: Statement,
  handoff: Statement,
  expertise: Statement,
  persona_reveal: Statement,
  casting_board: Statement,
  raw_finished: Statement,
  scene_placement: Statement,
  analysis_loader: AnalysisLoader,
  reveal: Statement,
  angle_engine: Statement,
  plan_reveal: Statement,
  teardown: Statement,
  email_capture: EmailCapture,
  paywall: Statement,
};

function Start() {
  const { s } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [answers, setAnswers] = useState<Answers>({});
  const [current, setCurrent] = useState<string>(s ?? FUNNEL.entry);
  /** The path actually taken, which is the only thing a back button can follow. */
  const [trail, setTrail] = useState<string[]>([]);
  const [restored, setRestored] = useState(false);
  /** Set once the funnel ends, which is a different state from running out of screens. */
  const [finished, setFinished] = useState<string | undefined>();

  /*
   * Progress survives leaving, which the original does too. The address wins
   * over what was stored — somebody who was sent a link to a screen means to
   * be on that screen — and what was stored wins over starting again.
   */
  useEffect(() => {
    if (restored) return;
    const saved = readSaved();
    if (saved) {
      setAnswers(saved.answers);
      setTrail(saved.trail ?? []);
      if (!s) setCurrent(saved.screen);
    }
    setRestored(true);
  }, [restored, s]);

  useEffect(() => {
    if (restored) writeSaved({ answers, screen: current, trail });
  }, [answers, current, restored, trail]);

  /*
   * A screen the engine named and this build does not have yet.
   *
   * It used to fall back to the entry screen, which is the worst possible
   * answer: somebody who finished the chapter was put back on the first screen
   * with their answers still in the funnel, and it read as the wizard throwing
   * them out. The funnel is shipped a chapter at a time, so running out of
   * screens is a normal state and has to look like one — the address stays on
   * the screen that does not exist, so a reload does not restart anything, and
   * the card says where the building got to.
   */
  const screen = screenById(FUNNEL, current);
  const entry = screenById(FUNNEL, FUNNEL.entry);
  if (!entry) throw new Error('the funnel has no entry screen');

  const chapter = progress(FUNNEL, screen?.id ?? current);
  const options = visibleOptions(FUNNEL, screen?.optionsRef, answers);
  const copy = Object.fromEntries(
    Object.entries(screen?.copy ?? {}).map(([key, value]) => {
      const chosen = resolve(value, answers);
      return [key, typeof chosen === 'string' ? interpolate(chosen, answers) : chosen];
    }),
  );

  const go = (to: string | undefined, next: Answers) => {
    if (!to) return;
    setFinished(undefined);
    setTrail((was) => [...was, current]);
    setCurrent(to);
    setAnswers(next);
    void navigate({ search: { s: to }, replace: false });
  };

  /**
   * Back, which follows the trail rather than the declaration.
   *
   * There is no «previous screen» in the data: the funnel branches, so two
   * people reach the same question from different places. Only the path taken
   * knows, and the answers stay — going back to change one is the whole point.
   */
  const back = () => {
    const previous = trail.at(-1);
    if (!previous) return;
    setTrail((was) => was.slice(0, -1));
    setCurrent(previous);
    void navigate({ search: { s: previous }, replace: false });
  };

  const answer = (value: string) => {
    if (!screen) return;
    const next = screen.binding ? { ...answers, [screen.binding]: value } : answers;
    setAnswers(next);
    // A single answer moves on by itself. The button stays for the people who
    // reach for it anyway, and for the screens that take more than one press.
    if (screen.autoAdvance) go(nextScreen(screen, next), next);
  };

  /** Several answers: pressing one on and off rather than replacing it. */
  const toggle = (key: string) => {
    if (!screen?.binding) return;
    const already = Array.isArray(answers[screen.binding])
      ? (answers[screen.binding] as string[])
      : [];
    const next = already.includes(key) ? already.filter((one) => one !== key) : [...already, key];
    setAnswers({ ...answers, [screen.binding]: next });
  };

  const Kind = screen ? KINDS[screen.kind] : undefined;
  const value = screen?.binding ? answers[screen.binding] : undefined;

  return (
    <FunnelFrame
      chapter={chapter.index}
      chapters={FUNNEL.chapters}
      onBack={trail.length > 0 ? back : undefined}
      /*
        A screen that advances by itself has **no button at all** — measured,
        and by arithmetic rather than by eye: the first screen is 54 + 712 + 84
        inside 850 and a question screen is 62 + 788, which leaves no room for
        a footer and no doubt about it. A disabled pill under a list that is
        going to move the moment you touch it reads as a screen that is stuck,
        which is the opposite of what it is.
      */
      footer={
        finished ? (
          <FunnelCta
            label="Create an account"
            onClick={() => {
              void navigate({ to: '/login', search: { redirectTo: '/welcome' } });
            }}
          />
        ) : !screen ? (
          /*
            The end of what is built, and the one thing this screen must not be
            is a dead end. Somebody has just spent two minutes answering, and
            leaving them on a card with no button is worse than not asking: the
            way on is an account, which is where the answers become a brand.
          */
          <div className="flex flex-col gap-2">
            <FunnelCta
              label="Create an account"
              onClick={() => {
                void navigate({ to: '/login', search: { redirectTo: '/welcome' } });
              }}
            />
            <button
              className="w-full cursor-pointer text-center text-[13px] leading-5 text-ink-muted hover:text-ink"
              data-testid="funnel-restart"
              onClick={() => {
                clearSaved();
                setAnswers({});
                setCurrent(FUNNEL.entry);
                void navigate({ search: {}, replace: true });
              }}
              type="button"
            >
              Start over
            </button>
          </div>
        ) : !screen.autoAdvance ? (
          <>
            <FunnelCta
              disabled={!canAdvance(screen, answers)}
              label={copy.cta ?? screen.cta.label}
              onClick={() => {
                const end = completion(screen, answers);
                if (end) setFinished(end.outcome);
                else go(nextScreen(screen, answers), answers);
              }}
            />
            {/*
              A screen that offers a way past itself says so in its copy, and
              the frame renders one wherever that copy exists — no new idea in
              the engine, and no screen-by-screen special case here.
            */}
            {copy.skip ? (
              <button
                className="mt-2 w-full cursor-pointer text-center text-[13px] leading-5 text-ink-muted hover:text-ink"
                data-testid="funnel-skip"
                onClick={() => go(nextScreen(screen, answers), answers)}
                type="button"
              >
                {copy.skip}
              </button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {finished ? (
        <TheEnd answers={answers} />
      ) : Kind && screen ? (
        <Kind
          copy={copy}
          onAnswer={answer}
          onToggle={toggle}
          options={options}
          title={copy.title ?? ''}
          value={typeof value === 'string' ? value : undefined}
          values={Array.isArray(value) ? value : []}
          why={copy.why}
        />
      ) : (
        <NoSuchScreen answers={answers} />
      )}
    </FunnelFrame>
  );
}

/**
 * An address naming a screen the funnel does not have.
 *
 * It was the end of what was built, back when the funnel arrived a chapter at
 * a time; all five are shipped now, so the only way here is a link that names
 * a screen — `?s=` is in the address and people edit addresses. The card is
 * kept rather than deleted because the failure it prevents is the same one it
 * always prevented: an unknown screen used to fall back to the first one, so a
 * mistyped link threw somebody out of a funnel they had half answered.
 *
 * It shows what they told us rather than an apology: a card that repeats the
 * answers back is the difference between "this went wrong" and "this is
 * broken".
 */
function NoSuchScreen({ answers }: { answers: Answers }) {
  /*
   * Answers read back in the words they were offered in, not in the keys they
   * are stored under. «ecommerce» and «own_site» are how the funnel talks to
   * itself; «Online shop» is what the person pressed, and a card that repeats
   * their answers back in a vocabulary they never saw is a card that proves
   * nothing was understood.
   */
  const labelled = (key: string, value: string): string => {
    const screen = FUNNEL.screens.find((one) => one.binding === key);
    const options = screen?.optionsRef ? FUNNEL.options[screen.optionsRef] : undefined;
    return options?.find((option) => option.key === value)?.label ?? value;
  };

  const given = Object.entries(answers)
    .filter(([, value]) => value)
    .map(([key, value]) => [
      key.replace(/_/g, ' '),
      (Array.isArray(value) ? value : [value as string])
        .map((one) => labelled(key, one))
        .join(', '),
    ]);

  return (
    <FunnelPanel>
      <FunnelQuestion
        title="There is no screen at that address"
        why="The link named a screen this funnel does not have. Nothing you answered is lost — it is all here."
      />
      {given.length > 0 ? (
        <dl className="mt-4 flex flex-col gap-2" data-testid="funnel-answers">
          {given.map(([key, value]) => (
            <div className="flex items-baseline justify-between gap-4" key={key}>
              <dt className="text-[12.5px] leading-[17.75px] text-ink-muted">{key}</dt>
              <dd className="text-right text-[15px] leading-[22.5px] font-[560] text-ink">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </FunnelPanel>
  );
}

/**
 * The end of the funnel, as opposed to the end of what is built.
 *
 * They look alike and are not: one is a boundary in the building, the other is
 * the point. Somebody who walked all thirty-eight screens is told they
 * finished, not that we have not finished.
 */
function TheEnd({ answers }: { answers: Answers }) {
  const name = typeof answers.biz_name === 'string' ? answers.biz_name : undefined;

  return (
    <FunnelPanel>
      <FunnelQuestion
        title={interpolate('The month is mapped for {{biz_name}}', { biz_name: name })}
        why="Every answer is saved. An account is what turns the plan into the brand it was written for."
      />
      <p className="mt-3 text-[15px] leading-[23.7px] text-ink" data-testid="funnel-finished">
        Nothing was generated and nothing was charged — that is the whole of what this rebuild does
        at the paywall.
      </p>
    </FunnelPanel>
  );
}

export const Route = createFileRoute('/start')({
  validateSearch: z.object({ s: z.string().optional() }),
  component: Start,
});
